import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import _ from "lodash";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcDatePickerFormik,
  OcSettingSwitchFormik,
} from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcSnackbar } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { useOcTwoFactorEnabledState } from "oc/hooks/use-two-factor-enabled-state";
import { twoFaEnabledDateState } from "oc/states/two-fa-enabled-date-state";
import * as Yup from "yup";

export const MyHrSystemSettingsLoginTwoFactorCard = () => {
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const isMobile = useOcIsMobile();
  const dateAt = useRecoilValue(twoFaEnabledDateState);
  const setDateAt = useSetRecoilState(twoFaEnabledDateState);
  const { setOpenMessage } = useOcSnackbar();

  const { state: twoFactorEnabled, setState: setTwoFactorEnabled } =
    useOcTwoFactorEnabledState();

  const [updateProperty, { loading }] = useMutation(OC_UPDATE_PROPERTY, {
    onError({ ...err }) {
      alert("error" + err.toString());
      setOpenMessage({
        type: "error",
        message: t("OC_PROPERTY_SAVE_FAILED"),
      });
    },
    onCompleted(data) {
      if (data?.updateProperty.key === "2faEnabled") {
        const parsedValue = data?.updateProperty.value === "true";
        setTwoFactorEnabled(parsedValue);
        setDateAt(data?.updateProperty.validFrom);
      }
      setOpenMessage({
        type: "success",
        message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
      });
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      enabled: twoFactorEnabled,
      validFrom: dateAt ? new Date(dateAt) : null,
    },
    validationSchema: Yup.object({
      validFrom: Yup.date()
        .nullable()
        .test({
          name: "validFromNotInPast",
          exclusive: false,
          message: t("OC_DATE_IN_FUTURE"),
          test: function (value) {
            if (!value) return true;
            const today = moment().startOf("day");
            const selectedDate = moment(value).startOf("day");
            return selectedDate.isSameOrAfter(today);
          },
        }),
    }),
    onSubmit: (values) => {
      handleSaveEnabled(values);
    },
  });

  const saveDisabled =
    formik.values.enabled === twoFactorEnabled &&
    _.isEqual(formik.values.validFrom, new Date(dateAt));

  interface FormValues {
    enabled: boolean;
    validFrom: Date | null;
  }

  const handleSaveEnabled = (values: FormValues) => {
    const { enabled, validFrom } = values;
    updateProperty({
      variables: {
        resource: "system",
        key: "2faEnabled",
        value: enabled.toString(),
        validFrom: enabled ? validFrom : null,
      },
    });
    setTwoFactorEnabled(enabled);
  };

  const handleCancel = () => {
    formik.resetForm();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleDialogClose = () => {
    formik.handleSubmit();
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleDialogClose}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_HR_TWO_FACTOR_AUTHENTICATION")}>
        <Box>
          <FormikProvider value={formik}>
            <Box width={isMobile ? "auto" : "600px"} padding="24px 0px">
              <OcSettingSwitchFormik
                name="enabled"
                id="enabled"
                label={t("MY_HR_TWO_FACTOR_AUTHENTICATION")}
                info={t("MY_HR_ENABLED_2FA")}
                isMobile={isMobile}
                disabled={false}
              />
              <Box
                sx={{ padding: isMobile ? "36px 0px 0px 0px" : "36px 16px" }}
              >
                <OcDatePickerFormik
                  name="validFrom"
                  id="validFrom"
                  label={t("MY_HR_REGISTRATION_DEADLINE")}
                  minDate={new Date()}
                  value={formik.values.enabled ? formik.values.validFrom : null}
                  disabled={!formik.values.enabled}
                />
              </Box>
            </Box>
            <OcCardButtonBar
              handleRightButton={handleCancel}
              rightButtonColor="secondary"
              rightButtonText={t("OC_CANCEL")}
              handlePrimaryButton={() => setShowConfirmDialog(true)}
              primaryButtonText={t("OC_SAVE")}
              primaryButtonDisabled={
                loading || saveDisabled || !formik.values.validFrom
              }
            />
          </FormikProvider>
        </Box>
      </OcCard>
    </>
  );
};

export default MyHrSystemSettingsLoginTwoFactorCard;
